import { useMemo } from 'react';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import {
	DEFAULT_ENVIRONMENT_DISPLAY_NAME,
	DEFAULT_UNIT_ID_DISPLAY_NAME,
	ENVIRONMENT_LABEL_KEY,
	UNIT_ID_LABEL_KEY
} from 'src/common/consts/platformConfigConsts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';

import { DASHBOARD_DATE_RANGE_OPTIONS } from '../../../constants/DashboardFiltersDefinitions';
import { processPlatformConfiguration } from '../../../helpers/ConfigurationProcessingHelpers';
import { createOptionsFromCoreLabels } from '../../../helpers/FilterOptionsHelpers';
import { useUniqueIssuesFilterOptions } from '../../../hooks/useUniqueIssuesFilterOptions';
import { getIssuesPageFilterDefinitions } from '../IssuesPageFilterDefinitions';

export const useIssuesPageFilters = (): {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
} => {
	const { configuration } = useConfiguration();

	const { data: apiFilterOptions } = useUniqueIssuesFilterOptions();

	const { filterDefinitions, filterOptions } = useMemo(() => {
		const severities = apiFilterOptions?.severities || [];
		const types = apiFilterOptions?.issueTypes || [];

		const baseFilterOptions: Record<string, OptionType[]> = {
			dateRange: DASHBOARD_DATE_RANGE_OPTIONS,
			severities,
			types
		};

		if (!configuration?.PLATFORM) {
			return {
				filterDefinitions: getIssuesPageFilterDefinitions(),
				filterOptions: baseFilterOptions
			};
		}

		const { coreLabels, isValid } = processPlatformConfiguration(configuration.PLATFORM);

		if (!isValid) {
			return {
				filterDefinitions: getIssuesPageFilterDefinitions(),
				filterOptions: baseFilterOptions
			};
		}

		const environmentLabel = coreLabels.find((label) => label.label === ENVIRONMENT_LABEL_KEY);
		const unitIdLabel = coreLabels.find((label) => label.label === UNIT_ID_LABEL_KEY);

		const environmentDisplayName =
			environmentLabel?.displayName || DEFAULT_ENVIRONMENT_DISPLAY_NAME;
		const unitIdDisplayName = unitIdLabel?.displayName || DEFAULT_UNIT_ID_DISPLAY_NAME;

		const coreLabelOptions = createOptionsFromCoreLabels(coreLabels, '', () => false);

		const filterDefinitions = getIssuesPageFilterDefinitions(
			unitIdDisplayName,
			environmentDisplayName
		);

		const filterOptions: Record<string, OptionType[]> = {
			...baseFilterOptions,
			...coreLabelOptions
		};

		return {
			filterDefinitions,
			filterOptions
		};
	}, [configuration, apiFilterOptions]);

	return { filterDefinitions, filterOptions };
};
